import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalState } from '../contexts/GlobalContext';
import { apiFetch } from '../utils/api';

const DEFAULT_STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved', 'Closed'];
const DEFAULT_PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent', 'Critical'];

function FeedbackReview() {
  const { state } = useGlobalState();
  const user = state.user;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('active');

  const isTitan = useMemo(() => user && user.role === 'Titan', [user]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await apiFetch('fetchFeedbackRequests');
        if (mounted) setItems(res.requests || []);
      } catch (e) {
        if (mounted) setError(e.message || 'Failed to load feedback');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (isTitan) load();
    return () => { mounted = false; };
  }, [isTitan]);

  const updateField = async (id, field, value) => {
    try {
      await apiFetch('updateFeedbackRequest', {
        method: 'POST',
        body: JSON.stringify({ id, [field]: value })
      });
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, [field]: value } : it));
    } catch (e) {
      alert(e.message || 'Update failed');
    }
  };

  const isCompletedStatus = (status = '') => {
    const normalized = status.trim().toLowerCase();
    return ['resolved', 'closed', 'completed', 'done'].includes(normalized);
  };

  // Build option lists that include any values present in DB
  const statusOptions = useMemo(() => {
    const present = Array.from(new Set(items.map(i => i.status).filter(Boolean)));
    const combined = [...DEFAULT_STATUS_OPTIONS, ...present];
    return Array.from(new Set(combined));
  }, [items]);

  const priorityOptions = useMemo(() => {
    const present = Array.from(new Set(items.map(i => i.priority).filter(Boolean)));
    const combined = [...DEFAULT_PRIORITY_OPTIONS, ...present];
    return Array.from(new Set(combined));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) =>
      activeTab === 'completed' ? isCompletedStatus(item.status) : !isCompletedStatus(item.status)
    );
  }, [items, activeTab]);

  if (!isTitan) return <div>Forbidden</div>;
  if (loading) return <div>Loading feedback…</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <div className="container-fluid py-3">
      <h2 className="mb-3">Feedback Review</h2>
      <div className="mb-3">
        <div className="btn-group" role="group" aria-label="Feedback filters">
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'active' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('active')}
          >
            Active
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'completed' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed
          </button>
        </div>
      </div>
      {filteredItems.length === 0 ? (
        <div className="text-muted">{activeTab === 'completed' ? 'No completed feedback yet.' : 'No active feedback to review.'}</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th>ID</th>
                <th>Created</th>
                <th>Type</th>
                <th>Title</th>
                <th>Description</th>
                <th>Reporter</th>
                <th>Status</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((it) => (
                <tr key={it.id}>
                  <td>{it.id}</td>
                  <td>{it.created_at}</td>
                  <td>{it.type}</td>
                  <td style={{minWidth: 200}}>{it.title}</td>
                  <td style={{whiteSpace: 'pre-wrap'}}>{it.description}</td>
                  <td>
                    {it.reporter_public_id ? (
                      <Link to={`/profile/${it.reporter_public_id}`}>
                        {it.reporter_name || '—'}
                      </Link>
                    ) : (
                      it.reporter_name || '—'
                    )}
                    <div className="text-muted" style={{fontSize: '0.85em'}}>{it.reporter_email || ''}</div>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={it.status || ''}
                      onChange={(e) => updateField(it.id, 'status', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {statusOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={it.priority || ''}
                      onChange={(e) => updateField(it.id, 'priority', e.target.value)}
                    >
                      <option value="">(none)</option>
                      {priorityOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FeedbackReview;
